import React, { useState } from 'react';
import { Sparkles, Send, X, Bot, User, PhoneCall, ExternalLink, ShieldCheck, ArrowRight } from 'lucide-react';
import { useStore } from '../../store/useStore';
import { askAIRecoveryAssistant, AIChatMessage } from '../../api/aiAssistant';
import { Link } from 'react-router-dom';

export const AIRecoveryChatModal: React.FC = () => {
  const { isAiModalOpen, closeAiModal } = useStore();
  const [messages, setMessages] = useState<AIChatMessage[]>([
    {
      id: 'welcome',
      sender: 'assistant',
      text: `G'day! I am your **Australian Identity Recovery Assistant**.\n\nI am trained strictly on official Australian government procedures (IDCARE, Services Australia, ATO, DVS, Cyber.gov.au).\n\nAsk me anything like:\n- *"Someone stole my NSW driver licence, what do I do?"*\n- *"How do I place a credit ban with Equifax?"*\n- *"My Medicare card was exposed in a breach, how do I get a new number?"*`,
      timestamp: new Date().toISOString()
    }
  ]);
  const [inputQuery, setInputQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isAiModalOpen) return null;

  const handleSend = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!inputQuery.trim() || isLoading) return;

    const userMsg: AIChatMessage = {
      id: 'user-' + Date.now(),
      sender: 'user',
      text: inputQuery.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMsg]);
    const currentInput = inputQuery;
    setInputQuery('');
    setIsLoading(true);

    try {
      const response = await askAIRecoveryAssistant(currentInput);
      setMessages(prev => [...prev, response]);
    } catch (err) {
      setMessages(prev => [
        ...prev,
        {
          id: 'err-' + Date.now(),
          sender: 'assistant',
          text: 'Sorry, I encountered an issue reaching the guidance database. Please check your network or call IDCARE directly on 1800 595 160.',
          timestamp: new Date().toISOString()
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePresetClick = (presetText: string) => {
    setInputQuery(presetText);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col h-[640px] max-h-[90vh] overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-800 bg-slate-950 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-500 flex items-center justify-center shadow-md">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-white text-base">AI Recovery Assistant</h3>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-950 text-emerald-300 border border-emerald-800/80 font-semibold uppercase">
                  Australian Guidance
                </span>
              </div>
              <p className="text-xs text-slate-400">Strictly verified against IDCARE, DVS & Cyber.gov.au procedures</p>
            </div>
          </div>
          <button
            onClick={closeAiModal}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Message stream */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex gap-3 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.sender === 'assistant' && (
                <div className="w-7 h-7 rounded-lg bg-emerald-800 flex items-center justify-center flex-shrink-0 mt-1">
                  <Bot className="w-4 h-4 text-emerald-200" />
                </div>
              )}

              <div className={`max-w-[85%] rounded-xl p-3.5 text-sm ${
                msg.sender === 'user'
                  ? 'bg-emerald-600 text-white rounded-br-none'
                  : 'bg-slate-800 border border-slate-700/80 text-slate-200 rounded-bl-none shadow-sm'
              }`}>
                <div className="whitespace-pre-line leading-relaxed">
                  {msg.text}
                </div>

                {/* Sources badge */}
                {msg.sources && msg.sources.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-700/70 space-y-1.5">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                      Authoritative Sources & Emergency Links:
                    </p>
                    {msg.sources.map((src, i) => (
                      <div key={i} className="flex flex-wrap items-center justify-between gap-1 text-xs bg-slate-900/80 px-2.5 py-1.5 rounded-lg border border-slate-700">
                        <span className="font-medium text-slate-300">{src.agency}</span>
                        <div className="flex items-center gap-2">
                          {src.phone && (
                            <a href={`tel:${src.phone.replace(/\s+/g, '')}`} className="text-emerald-400 font-mono hover:underline flex items-center gap-1">
                              <PhoneCall className="w-3 h-3" /> {src.phone}
                            </a>
                          )}
                          <a href={src.url} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline flex items-center gap-0.5">
                            Official Site <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Quick actions */}
                {msg.quickActions && msg.quickActions.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {msg.quickActions.map((qa, i) => (
                      <Link
                        key={i}
                        to={qa.url}
                        onClick={closeAiModal}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md bg-emerald-950 text-emerald-300 border border-emerald-700/60 hover:bg-emerald-900 transition-colors font-medium"
                      >
                        <span>{qa.label}</span>
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {msg.sender === 'user' && (
                <div className="w-7 h-7 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0 mt-1">
                  <User className="w-4 h-4 text-slate-300" />
                </div>
              )}
            </div>
          ))}

          {isLoading && (
            <div className="flex gap-3 items-center text-slate-400 text-xs py-2">
              <div className="w-7 h-7 rounded-lg bg-emerald-900/60 flex items-center justify-center animate-pulse">
                <Bot className="w-4 h-4 text-emerald-400" />
              </div>
              <span>Consulting verified Australian identity procedures...</span>
            </div>
          )}
        </div>

        {/* Preset suggestions */}
        <div className="px-4 py-2 bg-slate-950/60 border-t border-slate-800 flex gap-2 overflow-x-auto text-xs">
          <button
            onClick={() => handlePresetClick("Someone has my driver's licence. What should I do?")}
            className="whitespace-nowrap px-2.5 py-1 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
          >
            Driver Licence compromise
          </button>
          <button
            onClick={() => handlePresetClick("How do I place a credit ban with Equifax?")}
            className="whitespace-nowrap px-2.5 py-1 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
          >
            Place a Credit Ban
          </button>
          <button
            onClick={() => handlePresetClick("My Medicare card was exposed, how do I get a new one?")}
            className="whitespace-nowrap px-2.5 py-1 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
          >
            Medicare card replacement
          </button>
        </div>

        {/* Input Bar */}
        <form onSubmit={handleSend} className="p-3 bg-slate-950 border-t border-slate-800 flex gap-2">
          <input
            type="text"
            value={inputQuery}
            onChange={(e) => setInputQuery(e.target.value)}
            placeholder="Ask a question about Australian ID recovery or scams..."
            className="flex-1 px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:border-emerald-500"
          />
          <button
            type="submit"
            disabled={!inputQuery.trim() || isLoading}
            className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-xl font-medium text-sm flex items-center gap-1.5 transition-colors"
          >
            <span>Ask</span>
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
};
